/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { existsNestedPath, getArrayedNestedPath, getNestedObjectValues } from "@/lib/objectPath-helper";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { PageFilterOperationEnum } from "./page-filter-operation.enum";
import { PageFilterTypes } from "./page-filter-types.enum";

/**
 * Matches an ISO 8601 datetime with an explicit offset (`Z` or `+02:00`), as
 * opposed to a bare `YYYY-MM-DD` date. Mirrors the backend's
 * `processDateFilterHelper` shape-detection in `page-main-filter.factory.ts`
 * so client-side re-filtering stays consistent with the server: a bare date
 * truncates to the UTC calendar day, an ISO instant compares as the exact
 * instant it names.
 */
const IS_ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;

/**
 * Applies a list of page filters to a list of entities and returns the matching subset.
 *
 * This is the exact filter-matching predicate that used to live inline inside
 * `BaseStore.filtered` (`store/base.store.ts`), extracted into a standalone,
 * side-effect-light pure function so it can:
 * - run against an arbitrary filter list instead of only `this.filters` (used by
 *   `FilteredPager` to scope filters per-consumer instead of sharing the store singleton), and
 * - be unit-tested directly (entities + filters in, filtered array out) instead of
 *   only indirectly through a Vuex module instance.
 *
 * An entity is kept when it matches every filter (logical AND). A single filter matches
 * when at least one value found at its `key` path satisfies the filter operation.
 *
 * @param entities the entities to filter
 * @param filters the filters to apply (combine your visible and hidden filters before calling)
 * @returns the entities that match all filters. When `filters` is empty the input array is returned as-is.
 */
export function applyPageFilters<T>(entities: T[], filters: IPageFilterElement[]): T[] {
  if (!filters.length) {
    return entities;
  }

  return entities.filter((element) => {
    return filters.every((filter) => {
      const filterType = filter.type as PageFilterTypes;
      const filterPath = filter.key;
      const filterSpecifier = filter.specifier;

      let values: (string | boolean | number)[] = [];
      if (
        [
          PageFilterTypes.CUSTOM_FIELD_VALUE,
          PageFilterTypes.CUSTOM_FIELD_NUMBER_VALUE,
          PageFilterTypes.CUSTOM_FIELD_DATE_VALUE
        ].includes(filterType) &&
        filterSpecifier
      ) {
        /**
         * Special case for custom field filtering.
         * Look up the custom field. It is nested "one level above value" in an element of the array there.
         */
        const pathBits = filterPath.split(".");
        const pathEnd = pathBits.pop();
        const reducedPath = pathBits.join(".");
        const valuesAtReducedPath = getArrayedNestedPath(element, reducedPath);
        const customField = valuesAtReducedPath.find((f) => JSON.stringify(f).includes(filterSpecifier));

        if (!customField || !pathEnd) {
          return false;
        }

        // get values from custom field
        values = getArrayedNestedPath(customField, pathEnd);
      } else {
        values = getArrayedNestedPath(element, filterPath);
      }

      // Default to EQUAL WITHOUT mutating the caller's filter object. This helper is now
      // shared by `BaseStore.filtered` and every `FilteredPager`, and the same filter
      // instances may be reused across consumers or held in Vuex state, so a local default
      // keeps it referentially transparent and free of surprising side effects.
      const operation = filter.operation || PageFilterOperationEnum.EQUAL;

      let filterValue: string | boolean = filter.value;
      let dateDayRange: { start: string; end: string } | undefined;
      if ([PageFilterTypes.CUSTOM_FIELD_DATE_VALUE, PageFilterTypes.DATE].includes(filterType)) {
        if (IS_ISO_DATETIME_REGEX.test(filterValue as string)) {
          // An explicit instant: compare the exact instant, no day truncation —
          // matches the backend's ISO-instant branch in processDateFilterHelper
          // (page-main-filter.factory.ts), including for $eq. Both sides are
          // normalized to canonical ISO strings, which compare lexicographically
          // the same as chronologically.
          filterValue = new Date(filterValue).toISOString();
          values = values.map((v) => new Date(v as string).toISOString());
        } else {
          // A bare date: compare against the UTC calendar day's start/end instant —
          // matches the backend's bare-date branch in processDateFilterHelper
          // (page-main-filter.factory.ts), which clamps to setUTCHours(0,0,0,0) /
          // setUTCHours(23,59,59,999) rather than the viewer's local day.
          const dayStart = new Date(filterValue);
          dayStart.setUTCHours(0, 0, 0, 0);
          const dayEnd = new Date(filterValue);
          dayEnd.setUTCHours(23, 59, 59, 999);
          values = values.map((v) => new Date(v as string).toISOString());

          if (operation === PageFilterOperationEnum.EQUAL || operation === PageFilterOperationEnum.NOT_EQUAL) {
            // The backend expands bare-date $eq into ($gte start AND $lte end) — mirror
            // that as an explicit day-range check below instead of a single filterValue,
            // since $eq/$ne can't be expressed as one bound.
            dateDayRange = { start: dayStart.toISOString(), end: dayEnd.toISOString() };
          } else {
            // Mirrors the bound the backend passes to addDateFilter() per operation: the
            // inclusive operators use their "natural" bound ($gte -> start, $lte -> end),
            // while the strict operators use the opposite bound so the whole day is
            // excluded from the match ($gt -> end, $lt -> start).
            const bound =
              operation === PageFilterOperationEnum.GREATER_EQUAL || operation === PageFilterOperationEnum.LESS
                ? dayStart
                : dayEnd;
            filterValue = bound.toISOString();
          }
        }
      }

      if ([PageFilterTypes.BOOLEAN].includes(filterType)) {
        if (filterValue === "true") filterValue = true;
        if (filterValue === "false") filterValue = false;
      }

      if (operation === PageFilterOperationEnum.EXISTS) {
        // Array-typed fields (e.g. `assignees`) are always present once saved, so field
        // presence can't express "empty" — mirror the backend's processExistsFilter()
        // (page-main-filter.factory.ts), which treats $exists on an array field as "array is
        // non-empty" instead.
        const rawValue = getNestedObjectValues(element as Record<string, unknown>, filterPath);
        const exists = Array.isArray(rawValue) ? rawValue.length > 0 : existsNestedPath(element, filterPath);
        return filterValue === "true" ? exists : !exists;
      }

      if (dateDayRange) {
        const { start, end } = dateDayRange;
        const withinDay = values.some((v) => v >= start && v <= end);

        if (operation === PageFilterOperationEnum.NOT_EQUAL) {
          return !withinDay;
        }

        return withinDay;
      }

      return values.some((v) => {
        const objectValue = v;

        if (operation === "$eq") {
          // use weak comparison to allow for implicit type conversion (e.g. to compare "7" with 7)
          return objectValue == filterValue;
        } else if (operation === "$ne") {
          // use weak comparison
          return objectValue != filterValue;
        } else if (operation === "$gt") {
          return objectValue > filterValue;
        } else if (operation === "$lt") {
          return objectValue < filterValue;
        } else if (operation === "$gte") {
          return objectValue >= filterValue;
        } else if (operation === "$lte") {
          return objectValue <= filterValue;
        } else {
          return false;
        }
      });
    });
  });
}
